import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Loader2, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { RedirectCondition } from "./ConditionalRedirectSettings";


interface FormField {
  id?: string;
  name: string;
  type: string;
  label: string;
  placeholder: string;
  required: boolean;
  options?: string[];
}

interface BundleOption {
  id: string;
  name: string;
  quantity: number;
  price: number;
  originalPrice?: number;
  image?: string;
  badge?: string;
  discount?: string;
  isPopular?: boolean;
}

interface AdvancedFormPreviewProps {
  settings: {
    title: string;
    subtitle: string;
    fields: FormField[];
    buttonText: string;
    buttonColor: string;
    buttonTextColor: string;
    backgroundColor: string;
    textColor: string;
    borderRadius: string;
    padding: string;
    style: string;
    showSuccessMessage: boolean;
    successMessage: string;
    redirectUrl?: string;
    showColorPicker?: boolean;
    colorOptions?: string[];
    showQuantitySelector?: boolean;
    minQuantity?: number;
    maxQuantity?: number;
    showBundleOptions?: boolean;
    bundleOptions?: BundleOption[];
    bundleStyle?: "list" | "cards" | "grid";
    enableConditionalRedirects?: boolean;
    conditionalRedirects?: RedirectCondition[];
    productPrice?: number;
    guaranteeText?: string;
  };
}

export const AdvancedFormPreview = ({ settings }: AdvancedFormPreviewProps) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});
  const [selectedColor, setSelectedColor] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(settings.minQuantity || 1);
  const [selectedBundle, setSelectedBundle] = useState<string>("");

  const getFieldKey = (field: FormField, index: number) => {
    return field.name || field.id || `${field.type}_${index}`;
  };

  const handleInputChange = (fieldId: string, value: any) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleFileUpload = (fieldId: string, file: File) => {
    setUploadedFiles((prev) => ({ ...prev, [fieldId]: file }));
    handleInputChange(fieldId, file.name);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const missingFields: FormField[] = [];
      settings.fields.forEach((f, idx) => {
        if (f.required) {
          const key = getFieldKey(f, idx);
          if (!formData[key]) missingFields.push(f);
        }
      });

      if (missingFields.length > 0) {
        toast.error(`يرجى ملء جميع الحقول المطلوبة: ${missingFields.map((f) => f.label).join(", ")}`);
        setIsSubmitting(false);
        return;
      }

      const uploadedImageUrls: Record<string, string> = {};
      for (const [fieldId, file] of Object.entries(uploadedFiles)) {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch('/api/upload', {
          method: 'POST',
          body: formData
        });
        const json = await res.json();

        if (!json.error && json.data) {
          uploadedImageUrls[fieldId] = json.data.publicUrl;
        }
      }

      const submissionData = {
        ...formData,
        ...uploadedImageUrls,
        ...(settings.showColorPicker && { selectedColor }),
        ...(settings.showQuantitySelector && { quantity }),
        ...(settings.showBundleOptions && { selectedBundle }),
      };

      const landingPageId = new URLSearchParams(window.location.search).get("page_id");
      // Also try to get ID from path if not in query params (e.g. /funnel/:id)
      const pathId = window.location.pathname.split('/').pop();
      const finalPageId = landingPageId || pathId;

      if (finalPageId) {
        const res = await fetch('/api/landing_page_submissions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            landing_page_id: finalPageId,
            form_data: submissionData
          })
        });
        const json = await res.json();

        if (json.error) {
          console.error("Error saving submission:", json.error);
        }
      }

      // حساب رابط التحويل
      let targetUrl = settings.redirectUrl || "/thank-you";

      if (
        settings.enableConditionalRedirects &&
        settings.conditionalRedirects &&
        settings.conditionalRedirects.length > 0
      ) {
        const currentQuantity = quantity;
        const productPrice = settings.productPrice || 0;
        const totalAmount = currentQuantity * productPrice;

        for (const condition of settings.conditionalRedirects) {
          let testValue: any;

          switch (condition.type) {
            case "quantity":
              testValue = currentQuantity;
              break;
            case "amount":
              testValue = totalAmount;
              break;
            case "bundle":
              testValue = selectedBundle;
              break;
            case "color":
              testValue = selectedColor;
              break;
            case "country":
              // Assuming 'governorate' or 'country' field exists
              const countryField = settings.fields.find(f => f.label.includes("محافظة") || f.label.includes("دولة") || f.type === "select");
              if (countryField) {
                const key = getFieldKey(countryField, settings.fields.indexOf(countryField));
                testValue = formData[key];
              }
              break;
            case "field":
              if (condition.fieldId) {
                const field = settings.fields.find(f => f.id === condition.fieldId);
                if (field) {
                  const key = getFieldKey(field, settings.fields.indexOf(field));
                  testValue = formData[key];
                }
              }
              break;
          }

          let matches = false;

          switch (condition.operator) {
            case "equal":
              matches = testValue == condition.value;
              break;
            case "greater":
              matches = Number(testValue) > Number(condition.value);
              break;
            case "less":
              matches = Number(testValue) < Number(condition.value);
              break;
            case "greaterOrEqual":
              matches = Number(testValue) >= Number(condition.value);
              break;
            case "lessOrEqual":
              matches = Number(testValue) <= Number(condition.value);
              break;
            case "contains":
              matches = String(testValue).includes(String(condition.value));
              break;
            case "notEqual":
              matches = testValue != condition.value;
              break;
          }

          if (matches) {
            targetUrl = condition.redirectUrl;
            break;
          }
        }
      }

      setIsSuccess(true);
      toast.success(settings.successMessage || "تم إرسال النموذج بنجاح!");

      // التحويل بعد 2 ثانية (سواء مع رسالة النجاح أو بدونها)
      setTimeout(() => {
        window.location.href = targetUrl;
      }, settings.showSuccessMessage ? 2000 : 500);

      setIsSubmitting(false);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("حدث خطأ أثناء إرسال النموذج");
      setIsSubmitting(false);
    }
  };

  const styleClasses = {
    modern: "bg-white shadow-xl border border-gray-100 rounded-2xl",
    classic: "bg-white shadow-lg border-2",
    minimal: "bg-transparent shadow-none border-0",
    gradient: "bg-gradient-to-br from-primary/5 to-secondary/5 shadow-lg",
    card: "bg-white shadow-2xl border",
    "high-converting": "bg-white shadow-2xl border-2 border-primary/10 relative overflow-hidden",
  };

  const getInputClasses = (style: string) => {
    switch (style) {
      case "minimal":
        return "border-x-0 border-t-0 border-b-2 border-gray-200 rounded-none px-0 bg-transparent focus:border-primary focus:ring-0 shadow-none focus:scale-100 focus:translate-y-0";
      case "modern":
        return "bg-gray-50 border-gray-200 focus:bg-white focus:ring-2 focus:ring-primary/20 rounded-lg h-12 shadow-sm transition-all duration-200";
      case "high-converting":
        return "bg-white border-2 border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/10 rounded-lg h-12 font-medium";
      default:
        return "transition-all focus:scale-[1.02]";
    }
  };

  if (isSuccess && settings.showSuccessMessage) {
    return (
      <div
        className={`max-w-2xl mx-auto p-8 rounded-xl ${styleClasses[settings.style as keyof typeof styleClasses] || styleClasses.modern} animate-scale-in`}
        style={{
          padding: `${settings.padding}px`,
          borderRadius: `${settings.borderRadius}px`,
          backgroundColor: settings.backgroundColor,
          color: settings.textColor,
        }}
      >
        <div className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold">{settings.successMessage || "تم إرسال النموذج بنجاح!"}</h3>
          <p className="text-muted-foreground">شكراً لك! سنتواصل معك قريباً.</p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`max-w-2xl mx-auto p-8 rounded-xl ${styleClasses[settings.style as keyof typeof styleClasses] || styleClasses.modern} animate-fade-in`}
      style={{
        padding: `${settings.padding}px`,
        borderRadius: `${settings.borderRadius}px`,
        backgroundColor: settings.backgroundColor,
        color: settings.textColor,
      }}
    >
      {settings.title && <h2 className={`text-3xl font-bold text-center mb-2 ${settings.style === 'minimal' ? 'text-4xl tracking-tight' : ''}`}>{settings.title}</h2>}
      {settings.subtitle && <p className="text-center text-muted-foreground mb-6">{settings.subtitle}</p>}

      <div className="space-y-6">
        {settings.showColorPicker && (
          <div className="space-y-3 p-4 bg-muted/30 rounded-lg animate-fade-in">
            <label className="block text-sm font-bold">اختر اللون</label>
            <div className="flex flex-wrap gap-3">
              {settings.colorOptions?.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setSelectedColor(color)}
                  className={`w-12 h-12 rounded-full border-4 transition-all hover:scale-110 ${selectedColor === color ? "border-primary scale-110 shadow-lg" : "border-gray-200"
                    }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
            {selectedColor && <p className="text-sm text-muted-foreground">اللون المختار: {selectedColor}</p>}
          </div>
        )}

        {settings.showQuantitySelector && (
          <div className="space-y-3 p-4 bg-muted/30 rounded-lg animate-fade-in">
            <label className="block text-sm font-bold">الكمية</label>
            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => setQuantity(Math.max(settings.minQuantity || 1, quantity - 1))}
                className="w-12 h-12 rounded-lg bg-secondary hover:bg-secondary/80 flex items-center justify-center font-bold text-xl transition-all hover:scale-110"
              >
                -
              </button>
              <span className="text-3xl font-bold min-w-[4rem] text-center">{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity(Math.min(settings.maxQuantity || 99, quantity + 1))}
                className="w-12 h-12 rounded-lg bg-secondary hover:bg-secondary/80 flex items-center justify-center font-bold text-xl transition-all hover:scale-110"
              >
                +
              </button>
            </div>
          </div>
        )}

        {settings.showBundleOptions && settings.bundleOptions && settings.bundleOptions.length > 0 && (
          <div className="space-y-3 animate-fade-in">
            <label className="block text-sm font-bold">اختر الباندل 🎁</label>

            <div className={`grid gap-4 ${settings.bundleStyle === 'cards' ? 'grid-cols-1 md:grid-cols-3' :
              settings.bundleStyle === 'grid' ? 'grid-cols-2 md:grid-cols-4' :
                'grid-cols-1'
              }`}>
              {settings.bundleOptions.map((bundle) => (
                <button
                  key={bundle.id}
                  type="button"
                  onClick={() => setSelectedBundle(bundle.id)}
                  className={`relative transition-all text-right group ${selectedBundle === bundle.id
                    ? "border-primary bg-primary/5 shadow-lg ring-2 ring-primary ring-offset-2"
                    : "border-gray-200 hover:border-primary/50 hover:shadow-md"
                    } ${settings.bundleStyle === 'cards'
                      ? "flex flex-col rounded-xl overflow-hidden border-2"
                      : settings.bundleStyle === 'grid'
                        ? "flex flex-col p-3 rounded-lg border text-center items-center"
                        : "flex items-center gap-3 p-4 rounded-lg border-2"
                    }`}
                >
                  {/* Badge */}
                  {(bundle.badge || bundle.isPopular) && (
                    <div className={`absolute top-2 left-2 z-10 px-2 py-1 text-xs font-bold rounded-full text-white ${bundle.isPopular ? "bg-gradient-to-r from-amber-500 to-orange-500 animate-pulse" : "bg-primary"
                      }`}>
                      {bundle.badge || "الأكثر طلباً 🔥"}
                    </div>
                  )}

                  {/* Discount Tag */}
                  {bundle.discount && (
                    <div className="absolute top-2 right-2 z-10 px-2 py-1 text-xs font-bold bg-red-500 text-white rounded-full">
                      خصم {bundle.discount}
                    </div>
                  )}

                  {/* Image */}
                  {bundle.image && (
                    <div className={`${settings.bundleStyle === 'cards' ? "w-full h-48" :
                      settings.bundleStyle === 'grid' ? "w-20 h-20 mb-2" :
                        "w-16 h-16 shrink-0"
                      }`}>
                      <img
                        src={bundle.image}
                        alt={bundle.name}
                        className={`w-full h-full object-cover ${settings.bundleStyle === 'cards' ? "" : "rounded-lg"
                          }`}
                        loading="lazy"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className={`${settings.bundleStyle === 'cards' ? "p-4 w-full" :
                    settings.bundleStyle === 'grid' ? "w-full" :
                      "flex-1"
                    }`}>
                    <h4 className={`font-bold ${settings.bundleStyle === 'cards' ? "text-lg mb-1" :
                      settings.bundleStyle === 'grid' ? "text-sm mb-1" :
                        "text-lg"
                      }`}>
                      {bundle.name}
                    </h4>

                    {settings.bundleStyle !== 'grid' && (
                      <p className="text-sm text-muted-foreground mb-2">الكمية: {bundle.quantity}</p>
                    )}

                    <div className={`flex items-center gap-2 ${settings.bundleStyle === 'grid' ? "justify-center" : ""
                      }`}>
                      <span className="text-lg font-bold text-primary">{bundle.price} ج.م</span>
                      {bundle.originalPrice && (
                        <span className="text-sm line-through text-muted-foreground">
                          {bundle.originalPrice} ج.م
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Selection Indicator */}
                  <div className={`absolute ${settings.bundleStyle === 'cards' ? "bottom-4 left-4" :
                    settings.bundleStyle === 'grid' ? "top-2 right-2" :
                      "top-1/2 -translate-y-1/2 left-4"
                    }`}>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${selectedBundle === bundle.id ? "border-primary bg-primary text-white" : "border-gray-300"
                      }`}>
                      {selectedBundle === bundle.id && <Check className="w-4 h-4" />}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {settings.fields.map((field, index) => {
          const fieldKey = getFieldKey(field, index);
          return (
            <div key={fieldKey} className="space-y-2 animate-fade-in">
              <Label htmlFor={fieldKey} className="text-sm font-medium">
                {field.label}
                {field.required && <span className="text-destructive ml-1">*</span>}
              </Label>

              {field.type === "text" && (
                <Input
                  id={fieldKey}
                  type="text"
                  placeholder={field.placeholder}
                  value={formData[fieldKey] || ""}
                  onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                  required={field.required}
                  className={getInputClasses(settings.style)}
                />
              )}

              {field.type === "email" && (
                <Input
                  id={fieldKey}
                  type="email"
                  placeholder={field.placeholder}
                  value={formData[fieldKey] || ""}
                  onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                  required={field.required}
                  className={getInputClasses(settings.style)}
                />
              )}

              {field.type === "phone" && (
                <Input
                  id={fieldKey}
                  type="tel"
                  placeholder={field.placeholder}
                  value={formData[fieldKey] || ""}
                  onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                  required={field.required}
                  className={getInputClasses(settings.style)}
                />
              )}

              {field.type === "textarea" && (
                <Textarea
                  id={fieldKey}
                  placeholder={field.placeholder}
                  value={formData[fieldKey] || ""}
                  onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                  required={field.required}
                  rows={3}
                  className={getInputClasses(settings.style)}
                />
              )}

              {field.type === "select" && (
                <Select
                  value={formData[fieldKey]}
                  onValueChange={(value) => handleInputChange(fieldKey, value)}
                  required={field.required}
                >
                  <SelectTrigger className={getInputClasses(settings.style)}>
                    <SelectValue placeholder={field.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options?.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {field.type === "file" && (
                <div className="relative">
                  <Input
                    id={fieldKey}
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(fieldKey, file);
                    }}
                    className="hidden"
                  />
                  <label
                    htmlFor={fieldKey}
                    className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-all hover:scale-[1.02] ${settings.style === 'minimal' ? 'bg-transparent border-gray-300' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <Upload className="h-5 w-5" />
                    <span>{uploadedFiles[fieldKey]?.name || field.placeholder}</span>
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button
        type="submit"
        className={`w-full font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 mt-8 ${settings.style === 'minimal' ? 'rounded-none' : ''}`}
        style={{
          backgroundColor: settings.buttonColor,
          color: settings.buttonTextColor,
          padding: settings.padding,
          borderRadius: settings.style === 'minimal' ? '0' : settings.borderRadius,
        }}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            جاري الإرسال...
          </>
        ) : (
          settings.buttonText
        )}
      </Button>

      {/* Trust Elements for High-Converting Style */}
      {settings.style === "high-converting" && (
        <div className="space-y-3 pt-2">
          {settings.guaranteeText && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <ShieldCheck className="w-4 h-4 text-green-600" />
              <span>{settings.guaranteeText}</span>
            </div>
          )}
          <div className="flex justify-center gap-4 opacity-70 grayscale hover:grayscale-0 transition-all">
            {/* Placeholder for payment icons - can be replaced with actual SVGs or images */}
            <div className="flex gap-2 text-xs text-gray-400">
              <span className="border px-1 rounded">🔒 Secure Payment</span>
              <span className="border px-1 rounded">💳 SSL Encrypted</span>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};
